import { filter, map, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { DoodModalModel } from '@core/models/modal.model';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalStoreDispatcher } from '@common/dispatchers/modal.dispatcher';
import { NonOptionalFields } from '@shared/utils/types/non-optional-fields';

export type ModalConfig<ModalIOConfig extends ModalIOConstraints> = ModalIOConfig;

/**
 * Properties that must be implemented By the modal component.
 * Use like:
 *  ```typescript
@Component({
  selector: 'app-zerosix-offered-product-picker-modal',
  templateUrl: './zerosix-offered-product-picker-modal.component.html',
})
export class ZerosixOfferedProductPickerModalComponent
  extends ModalScrollBlockBase
  implements OnDestroy, GetModalInterface<ZerosixOfferedProductPickerModalConfig>
{
  ...
}
 */
export type GetModalInterface<ModalIOConfig extends ModalIOConstraints> =
  ModalIOConfig['inputs'] extends object ? NonOptionalFields<ModalIOConfig['inputs']> : {};

type ModalInputs = Record<string, unknown>;

type ModalIOConstraints = {
  inputs?: ModalInputs;
  outputs?: Record<string, unknown>;
};

type ModalIOConstraintsDefault = {
  inputs: {};
  outputs: {};
};

/**
 * Usage example:
 *
 * 1. Define the modal's inputs and outputs:
 * ```typescript
export type ShopSearchParametersModalConfig = ModalConfig<{
  inputs: ShopSearchParametersModalInputs;
  outputs: ShopSearchParametersModalEvents;
}>;

type ShopSearchParametersModalInputs = {
  overrideSelectedDistributionMode?: string;
};

type ShopSearchParametersModalEvents = {
  type: 'updated';
  success: boolean;
};
 *
 * ```
 *
 * 2. Emit an event:
 * ```typescript
 * this.modalsService.emitEvent({ type: 'updated', success: true });
 * ```
 *
 * 3. Retrieve the input data:
 * ```typescript
 * data$ = this.modalsService.getData(ShopSearchParametersModalComponent.handle);
 * ```
 *
 * 4. From the component that opens the modal, inject `ModalsService`, and pass the modal config:
 * ```typescript
 * private readonly modalsService: ModalsService<ShopSearchParametersModalConfig>;
 * ```
 *
 * 5. Listen to the events:
 * ```typescript
 * event$ = this.modalsService.event$(handle);
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ModalsService<
  MainModalIOConfig extends ModalIOConstraints = ModalIOConstraintsDefault,
> {
  private subjectSource = new Subject<{ handle: string; event: MainModalIOConfig['outputs'] }>();
  /**
   * Listen to the open modal events.
   * Limitation if the same modal (same handle) is opened multiple times, the event will be emitted
   * for all of them.
   */
  _event$ = (handle: string) =>
    this.subjectSource.pipe(
      filter(({ handle: h }) => h === handle),
      map(({ event }) => event),
    );

  event$<ModalOutputs extends ModalIOConstraints = MainModalIOConfig>(
    handle: string,
  ): Observable<ModalOutputs['outputs']> {
    return this._event$(handle) as unknown as Observable<ModalOutputs['outputs']>;
  }

  constructor(
    private modalSelector: ModalStoreSelector,
    private modalDispatcher: ModalStoreDispatcher,
  ) {}

  open<ModalIOConfig extends ModalIOConstraints>(
    handle: string,
    attribute?: string,
    data?: ModalIOConfig['inputs'],
  ): void;
  open(handle: string, attribute?: string, data?: MainModalIOConfig['inputs']): void {
    const allModals = this.modalSelector.modals;
    const lastModal = allModals[0];
    this.modalDispatcher.addModal({
      handle,
      attribute,
      data,
      index: ((lastModal?.index ? lastModal.index / 10 : 0) + 1) * 10,
    });
  }

  close(handle: string): void {
    this.modalDispatcher.removeModal(handle);
  }

  isModalLink(link: string): boolean {
    return link.startsWith('modal://');
  }

  openFromLink(link: string): void {
    const urlWithHttp = link.replace('modal://', 'http://');
    const url = new URL(urlWithHttp);
    const params = new URLSearchParams(url.search);
    const inputs: { [key: string]: unknown } = {};
    params.forEach((value, key) => {
      inputs[key] = value;
    });
    const handle = url.host;
    this.open(handle, undefined, inputs as MainModalIOConfig['inputs']);
  }

  setData(handle: string, data: MainModalIOConfig['inputs']): void {
    this.modalDispatcher.updateModal({
      id: handle,
      changes: { data },
    });
  }

  getData(handle: string): Observable<DoodModalModel<MainModalIOConfig['inputs']> | undefined> {
    return this.modalSelector.selectModal(handle);
  }

  emitEvent({ data, handle }: { data: MainModalIOConfig['outputs']; handle: string }): void {
    this.subjectSource.next({ handle, event: data });
  }
}
